import React, { useState, useEffect } from 'react';
import { getState, setState as setGlobalState, subscribe } from '../lib/userStore';
import AIAssistant from '../lib/components/AIAssistant';


const CARRIER_NAMES = { bcbs: 'BlueCross BlueShield of SC', aetna: 'Aetna', cigna: 'Cigna', united: 'UnitedHealthcare', humana: 'Humana', medicaid: 'SC Medicaid', medicare: 'Medicare' };
const PLAN_NAMES    = { ppo: 'PPO', hmo: 'HMO', hdhp: 'HDHP', epo: 'EPO' };

function ageFromDob(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate())) age--;
  return age > 0 ? String(age) : null;
}

export default function ProfileScreen({ navigate, goBack, user, saveProfile, signOut }) {
  const init = getState();
  const [medications, setMedications] = useState(init.medications || []);
  const [firstName, setFirstName] = useState(init.firstName || '');
  const [lastName,  setLastName]  = useState(init.lastName  || '');
  const [zipCode,   setZipCode]   = useState(init.zipCode   || '');
  const [dob,       setDob]       = useState(init.dob       || '');
  const [gender,    setGender]    = useState(init.gender    || '');
  const [carrier,   setCarrier]   = useState(init.carrier   || '');
  const [planType,  setPlanType]  = useState(init.planType  || '');
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);

  useEffect(() => {
    return subscribe(s => {
      if (s.firstName    !== undefined) setFirstName(s.firstName    || '');
      if (s.lastName     !== undefined) setLastName(s.lastName      || '');
      if (s.zipCode      !== undefined) setZipCode(s.zipCode        || '');
      if (s.dob          !== undefined) setDob(s.dob                || '');
      if (s.gender       !== undefined) setGender(s.gender          || '');
      if (s.carrier      !== undefined) setCarrier(s.carrier        || '');
      if (s.planType     !== undefined) setPlanType(s.planType      || '');
      if (s.medications  !== undefined) setMedications(s.medications || []);
    });
  }, []);

  async function handleSave() {
    setSaving(true);
    const age = ageFromDob(dob);
    setGlobalState({ firstName, lastName, zipCode, dob, age, gender });
    await saveProfile({ firstName, lastName, zipCode, dob, age, gender });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const carrierLabel = carrier ? (CARRIER_NAMES[carrier] || carrier) : null;
  const planLabel    = planType ? (PLAN_NAMES[planType]  || planType) : null;

  return (
    <div style={s.screen}>
      {/* Header */}
      <div style={s.header}>
        <div style={{ ...s.accentBar, background: 'linear-gradient(90deg, var(--teal), var(--teal-2))' }} />
        <button style={s.back} onClick={goBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div style={s.title}>My Profile</div>
        <div style={s.subtitle}>{user?.email}</div>
      </div>

      <div style={s.body}>
        <div style={s.bodyInner}>

          {/* Personal info */}
          <div style={s.sectionLabel}>Personal info</div>
          <div style={s.card}>
            <div style={s.cardInner}>
              <div style={s.row}>
                <div style={{ ...s.fieldGroup, flex: 1 }}>
                  <div style={s.fieldLabel}>First name</div>
                  <input style={s.input} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
                </div>
                <div style={{ ...s.fieldGroup, flex: 1 }}>
                  <div style={s.fieldLabel}>Last name</div>
                  <input style={s.input} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
                </div>
              </div>
              <div style={s.fieldGroup}>
                <div style={s.fieldLabel}>Zip code</div>
                <input
                  style={s.input}
                  type="tel"
                  inputMode="numeric"
                  maxLength={5}
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="e.g. 29601"
                />
              </div>
              <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save changes'}
              </button>
            </div>
          </div>

          {/* Optional details */}
          <div style={s.sectionLabel}>
            Optional details
            <span style={s.optionalBadge}>Optional</span>
          </div>
          <div style={s.card}>
            <div style={s.cardInner}>
              <div style={s.fieldGroup}>
                <div style={s.fieldLabel}>Date of birth</div>
                <input
                  style={{ ...s.input, color: dob ? 'var(--ink)' : 'var(--ink-4)' }}
                  type="date"
                  value={dob}
                  onChange={e => setDob(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div style={s.fieldGroup}>
                <div style={s.fieldLabel}>Biological sex</div>
                <select style={s.select} value={gender} onChange={e => setGender(e.target.value)}>
                  <option value="">Select…</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="prefer_not">Prefer not to say</option>
                </select>
              </div>
              <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save changes'}
              </button>
            </div>
          </div>

          {/* Insurance (read-only — edit in Coverage tab) */}
          <div style={s.sectionLabel}>Insurance</div>
          {carrierLabel && planLabel ? (
            <div style={s.card}>
              <div style={s.insuranceInner}>
                <div style={s.insuranceInfo}>
                  <div style={s.insurancePlan}>{carrierLabel}</div>
                  <div style={s.insurancePlanType}>{planLabel}</div>
                </div>
                <button style={s.editInsuranceBtn} onClick={() => navigate('insurance')}>
                  Edit coverage →
                </button>
              </div>
            </div>
          ) : (
            <div style={s.card}>
              <div style={s.insuranceEmpty}>
                <div style={s.insuranceEmptyText}>No insurance added yet.</div>
                <button style={s.editInsuranceBtn} onClick={() => navigate('insurance')}>Add coverage →</button>
              </div>
            </div>
          )}

          {/* Medications */}
          <div style={s.sectionLabel}>My Medications</div>
          <div style={s.card}>
            <button style={s.medsRow} onClick={() => navigate('medications')}>
              <div style={s.medsInfo}>
                <div style={s.medsTitle}>
                  {medications.length > 0
                    ? `${medications.length} medication${medications.length !== 1 ? 's' : ''} tracked`
                    : 'No medications added'}
                </div>
                <div style={s.medsSub}>Compare costs · ask AI about your prescriptions</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <path d="M6 4l4 4-4 4" stroke="var(--ink-4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* AI Assistant */}
          <div style={s.sectionLabel}>Health assistant</div>
          <AIAssistant placeholder="Ask about your coverage, screenings, or finding care…" />

          {/* Sign out */}
          <div style={s.sectionLabel}>Account</div>
          <button style={s.signOutBtn} onClick={signOut}>Sign out</button>

          <div style={{ height: '8px' }} />
        </div>
      </div>
    </div>
  );
}

const s = {
  screen: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--cream)' },
  header: { padding: '52px 24px 20px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', position: 'relative', flexShrink: 0 },
  accentBar: { position: 'absolute', top: 0, left: 0, right: 0, height: '3px' },
  back: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, color: 'var(--ink-3)', cursor: 'pointer', marginBottom: '16px', background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-body)' },
  title: { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '26px', color: 'var(--ink)', letterSpacing: '-0.5px', lineHeight: 1.2 },
  subtitle: { fontSize: '13px', color: 'var(--ink-4)', marginTop: '4px' },
  body: { flex: 1, minHeight: 0, overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' },
  bodyInner: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px' },
  sectionLabel:  { fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: '-4px', display: 'flex', alignItems: 'center', gap: '8px' },
  optionalBadge: { fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'var(--cream-2)', color: 'var(--ink-4)', padding: '2px 7px', borderRadius: '100px', border: '1px solid var(--border)' },
  card: { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden' },
  cardInner: { padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' },
  row: { display: 'flex', gap: '10px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldLabel: { fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)' },
  input: { width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--cream)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink)', outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--cream)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink)', outline: 'none', appearance: 'none', boxSizing: 'border-box' },
  saveBtn: { width: '100%', padding: '13px 18px', background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
  insuranceInner: { padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' },
  insuranceInfo: {},
  insurancePlan: { fontSize: '14px', fontWeight: 600, color: 'var(--ink)', marginBottom: '2px' },
  insurancePlanType: { fontSize: '12px', color: 'var(--amber)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' },
  editInsuranceBtn: { fontSize: '13px', fontWeight: 600, color: 'var(--amber)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0, flexShrink: 0 },
  insuranceEmpty: { padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  insuranceEmptyText: { fontSize: '13.5px', color: 'var(--ink-4)' },
  signOutBtn: { width: '100%', padding: '14px', background: 'var(--surface)', border: '1.5px solid var(--border-strong)', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, color: 'var(--ink-3)', cursor: 'pointer' },
  medsRow:    { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left', gap: '12px' },
  medsInfo:   { flex: 1 },
  medsTitle:  { fontSize: '14px', fontWeight: 600, color: 'var(--ink)', marginBottom: '2px' },
  medsSub:    { fontSize: '12px', color: 'var(--ink-4)' },
};
